import { useCategories } from "../hooks/useCategories";
import ServicesSection from "../components/services/ServicesSection";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowUpRight, Star, Phone, Mail, MessageCircle, Clock, ArrowRight, Sparkles, Paperclip, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import {
  WHY_CHOOSE,
  STEPS,
  FAQS,
} from "@/lib/services";
import { categoryDestination } from "../lib/categoryRoutes";
import HomeTrending from "../components/site/HomeTrending";
import FirebaseReviews from "../components/site/FirebaseReviews";
import HomeAccountRail from "../components/site/HomeAccountRail";
import WebOfferCarousel from "../components/site/WebOfferCarousel";
import CelebrityDesignCarousel from "../components/site/CelebrityDesignCarousel";
import StichBEssentials from "../components/site/StichBEssentials";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [contactAttachments, setContactAttachments] = useState([]);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);
  const [contactLoading, setContactLoading] = useState(false);

  const {
  categories,
  categoriesLoading,
  categoriesError,
} = useCategories();

  const openServiceDetail = (category) => {
    navigate(categoryDestination(category));
  };
  const openSection = (category, section) => navigate(`/category/${category.id}/section/${section.id}`);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill required fields");
      return;
    }
    setContactLoading(true);
    try {
      const payload = new FormData();
      Object.entries(contactForm).forEach(([key, value]) => payload.append(key, value));
      contactAttachments.forEach((file) => payload.append("attachments", file));
      const response = await axios.post(`${API}/contact`, payload);
      toast.success(response.data.email_sent
        ? "Message emailed to care@stichb.com."
        : "Message saved. Email delivery is pending configuration.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
      setContactAttachments([]);
      setAttachmentInputKey((value) => value + 1);
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div id="top" className="relative bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/services")} />

      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-28 overflow-hidden" data-testid="hero-section">
        <HomeAccountRail />
        <div className="absolute top-24 right-0 w-80 h-80 bg-[#FFE0C7] rounded-full blur-3xl opacity-60 -z-0" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FFEDDD] rounded-full blur-3xl opacity-70 -z-0" />

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="fade-up inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-[#F4E9DB] px-4 py-2 rounded-full text-xs font-semibold text-[#F46524] mb-6">
              <Sparkles size={14} /> STICHB · Your Plan B Tailor
            </div>
            <h1 className="fade-up d1 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2B2B2B]">
              Perfect Fit.<br />
              Expert Stitching.<br />
              <span className="text-[#F46524]">Delivered</span> to your doorstep.
            </h1>
            <p className="fade-up d2 mt-8 text-lg text-[#5b5a56] max-w-xl leading-relaxed">
              From custom tailoring to precision alterations, StichB brings professional stitching right to your home.
              Everyday wear, festive outfits, office attire, designer creations — every stitch reflects quality,
              comfort and style.
            </p>
            <div className="fade-up d3 mt-10 flex flex-col sm:flex-row gap-4">
              <button onClick={() => navigate("/services")} data-testid="hero-book-service" className="btn-primary">
                Book a Service <ArrowRight size={18} />
              </button>
              <button onClick={() => navigate("/services/home-measurement")} data-testid="hero-schedule-pickup" className="btn-ghost">
                Schedule Home Visit <ArrowUpRight size={18} />
              </button>
            </div>

            <div className="fade-up d4 mt-14 flex flex-wrap gap-8 md:gap-12">
              <div>
                <div className="font-display text-3xl md:text-4xl text-[#F46524]">2,400+</div>
                <div className="text-xs text-[#8a8781] font-medium mt-1">Outfits stitched</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl text-[#F46524]">4.9★</div>
                <div className="text-xs text-[#8a8781] font-medium mt-1">Client rating</div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl text-[#F46524]">48hr</div>
                <div className="text-xs text-[#8a8781] font-medium mt-1">Express option</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[24px] overflow-hidden card-soft">
              <img
                src="https://images.pexels.com/photos/9983579/pexels-photo-9983579.jpeg?auto=compress&cs=tinysrgb&w=1000"
                alt="Tailor stitching fabric"
                fetchPriority="high"
                className="block w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-white rounded-2xl p-5 shadow-xl border border-[#F4E9DB] max-w-[240px] hidden md:block">
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="fill-[#F46524] text-[#F46524]" />
                ))}
              </div>
              <div className="text-xs font-semibold text-[#2B2B2B]">"Perfect fit, on time — worth every rupee."</div>
              <div className="text-[10px] text-[#8a8781] mt-1">— Meera, Bengaluru</div>
            </div>
            <div className="absolute -top-4 -right-4 bg-[#F46524] text-white rounded-2xl px-5 py-4 shadow-xl hidden md:block">
              <div className="text-[10px] font-semibold opacity-80">FROM</div>
              <div className="font-display text-2xl">₹399</div>
              <div className="text-[10px] font-medium">/garment</div>
            </div>
          </div>
        </div>
      </section>

      <WebOfferCarousel />
      <CelebrityDesignCarousel />
      <StichBEssentials />

      {/* MARQUEE */}
      <div className="border-y border-[#F4E9DB] py-5 overflow-hidden bg-white">
        <div className="marquee-track flex gap-16 whitespace-nowrap text-sm font-semibold text-[#F46524]">
          {Array(2).fill(0).map((_, i) => (
            <div key={i} className="flex gap-16">
              {["Bridal Wear", "Blazers", "Anarkalis", "Sherwanis", "Lehengas", "Kurtis", "Home Furnishing", "Alterations", "Custom Designs", "School Uniforms"].map((t) => (
                <span key={t} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F46524]" /> {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <ServicesSection
        categories={categories}
        loading={categoriesLoading}
        error={categoriesError}
        onCategoryClick={openServiceDetail}
        onSectionClick={openSection}
      />

      {/* TRENDING */}
      <section id="gallery" className="py-16 md:py-20 bg-white" data-testid="gallery-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
            <div className="max-w-2xl"><div className="font-label text-[#F46524] mb-4">Trending</div><h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">Popular services and trending designs.</h2></div>
            <p className="text-[#5b5a56] max-w-sm">Discover what customers are booking and choose an active design to begin your order.</p>
          </div>
          <HomeTrending />
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 md:py-32 bg-white" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-label text-[#F46524] mb-4">Why StichB</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">
              Every outfit deserves <span className="text-[#F46524]">expert</span> craftsmanship.
            </h2>
            <p className="text-[#5b5a56] mt-6 leading-relaxed max-w-md">
              Every customer deserves exceptional service. Stitching is more than joining fabric — it's creating
              confidence, comfort and style that fits perfectly.
            </p>
            <div className="mt-10 rounded-3xl overflow-hidden max-w-md">
              <img
              src="https://images.unsplash.com/photo-1739797725182-9823eace187e?auto=format&fit=crop&q=70&w=800"
              alt="Expert tailor"
              loading="lazy"
              className="block w-full h-auto"
            />
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((w, i) => (
              <div key={w} data-testid={`why-item-${i}`} className="card-soft p-6 flex items-start gap-4 lift">
                <div className="w-11 h-11 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0">
                  <Check size={18} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-display text-lg">{w}</div>
                  <div className="text-sm text-[#5b5a56] mt-1">
                    {["Master tailors with 10+ years of experience.","Doorstep pickup and drop across the city.","Fabric-first finishing on every seam.","Clear, upfront pricing — no surprises.","Committed delivery dates, always honoured.","Every stitch to your exact measurements.","Buttons, borders, linings — all finished right.","You leave happy or we make it right."][i]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="process" className="py-24 md:py-32" data-testid="process-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="font-label text-[#F46524] mb-4">How it works</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">
              Four steps. <span className="text-[#F46524]">Zero hassle.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`step-${i + 1}`} className="card-soft p-8 lift relative">
                <div className="step-circle mb-6">{s.n}</div>
                <h3 className="font-display text-xl mb-2">{s.title}</h3>
                <p className="text-[#5b5a56] text-sm leading-relaxed">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-14 -right-3 text-[#F46524] opacity-40">
                    <ArrowRight size={22} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE / REVIEWS */}
      <section id="reviews" className="py-24 md:py-32 bg-white" data-testid="reviews-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-14 gap-6">
            <div>
              <div className="font-label text-[#F46524] mb-4">Loved by clients</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">Real stitches. Real reviews.</h2>
            </div>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-[#F46524] text-[#F46524]" />)}
              <span className="ml-2 font-display text-2xl">4.9</span>
              <span className="text-[#8a8781] text-sm">/ 5 · 500+ reviews</span>
            </div>
          </div>
          <FirebaseReviews />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32" data-testid="faq-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="font-label text-[#F46524] mb-4">FAQ</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              Answers to what you're <span className="text-[#F46524]">wondering</span>.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="card-soft border-0 px-6 rounded-2xl"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="font-display text-lg md:text-xl text-left hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5b5a56] text-base leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-24" data-testid="final-cta">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="rounded-3xl bg-gradient-to-br from-[#F46524] to-[#FF8A3D] p-10 md:p-16 text-white text-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10" />
            <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-white/10" />
            <div className="relative z-10">
              <div className="font-label mb-4 opacity-90">Ready when you are</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl mx-auto">
                Ready to stitch something beautiful?
              </h2>
              <p className="mt-6 text-base md:text-lg opacity-90 max-w-xl mx-auto">
                Hassle-free tailoring with professional craftsmanship, personalised service and doorstep convenience.
              </p>
              <button
                onClick={() => navigate("/category/C7zT1ibizIsxMOjfECP8")}
                data-testid="final-cta-book"
                className="mt-10 inline-flex items-center gap-3 bg-white text-[#F46524] px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                Book Your Stitching Service Today <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-label text-[#F46524] mb-4">Contact Us</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
              We'd love to <span className="text-[#F46524]">hear</span> from you.
            </h2>

            <div className="mt-12 space-y-6">
              {[
                { icon: Phone, label: "Phone", value: "+91 97013 38283", href: "tel:+919701338283" },
                { icon: MessageCircle, label: "WhatsApp", value: "+91 97013 38283", href: "https://wa.me/919701338283" },
                { icon: Mail, label: "Email", value: "care@stichb.com", href: "mailto:care@stichb.com" },
              ].map((c) => (
                <a key={c.label} href={c.href} target={c.label === "WhatsApp" ? "_blank" : undefined} rel={c.label === "WhatsApp" ? "noreferrer" : undefined} className="flex items-start gap-4 card-soft p-5 lift">
                  <div className="w-11 h-11 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0">
                    <c.icon size={18} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-[#8a8781] uppercase tracking-wider">{c.label}</div>
                    <div className="font-display text-lg mt-1">{c.value}</div>
                  </div>
                </a>
              ))}
              <div className="flex items-start gap-4 card-soft p-5">
                <div className="w-11 h-11 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0">
                  <Clock size={18} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#8a8781] uppercase tracking-wider">Business Hours</div>
                  <div className="font-semibold mt-1">Mon – Sat · 9:00 AM – 8:00 PM</div>
                  <div className="text-sm text-[#8a8781]">Sunday · By Appointment Only</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submitContact} className="card-soft p-8 md:p-10" data-testid="contact-form">
              <div className="font-display text-2xl mb-2">Send us a message</div>
              <p className="text-[#8a8781] text-sm mb-8">We usually respond within a few hours.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold block mb-2 text-[#2B2B2B]">Name *</label>
                  <input
                    data-testid="contact-input-name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full bg-[#FFF8F2] rounded-xl border border-[#F4E9DB] focus:border-[#F46524] px-4 py-3 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-2 text-[#2B2B2B]">Email *</label>
                  <input
                    type="email"
                    data-testid="contact-input-email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full bg-[#FFF8F2] rounded-xl border border-[#F4E9DB] focus:border-[#F46524] px-4 py-3 outline-none text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold block mb-2 text-[#2B2B2B]">Phone</label>
                  <input
                    data-testid="contact-input-phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="w-full bg-[#FFF8F2] rounded-xl border border-[#F4E9DB] focus:border-[#F46524] px-4 py-3 outline-none text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold block mb-2 text-[#2B2B2B]">Message *</label>
                  <textarea
                    data-testid="contact-input-message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows={4}
                    className="w-full bg-[#FFF8F2] rounded-xl border border-[#F4E9DB] focus:border-[#F46524] px-4 py-3 outline-none text-sm resize-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold block mb-2 text-[#2B2B2B]">Attachments</label>
                  <label className="inline-flex items-center gap-2 border border-[#F4E9DB] bg-[#FFF8F2] px-4 py-3 text-sm font-semibold cursor-pointer hover:border-[#F46524]">
                    <Paperclip size={17} /> Add images
                    <input
                      key={attachmentInputKey}
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={(event) => {
                        const selected = Array.from(event.target.files || []);
                        if (contactAttachments.length + selected.length > 5) {
                          toast.error("You can attach up to 5 images.");
                          return;
                        }
                        if (selected.some((file) => file.size > 8 * 1024 * 1024)) {
                          toast.error("Each image must be 8 MB or smaller.");
                          return;
                        }
                        setContactAttachments((current) => [...current, ...selected]);
                      }}
                    />
                  </label>
                  {contactAttachments.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {contactAttachments.map((file, index) => (
                        <span key={`${file.name}-${file.lastModified}-${index}`} className="inline-flex max-w-full items-center gap-2 border border-[#F4E9DB] bg-white px-3 py-2 text-xs">
                          <span className="truncate max-w-52">{file.name}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${file.name}`}
                            onClick={() => setContactAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#8a8781]">Up to 5 images, 8 MB each.</p>
                </div>
              </div>
              <button type="submit" disabled={contactLoading} data-testid="contact-submit" className="btn-primary mt-8 disabled:opacity-60">
                {contactLoading ? "Sending..." : "Send Message"} <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

    </div>
  );
}
